#!/usr/bin/env node
/**
 * BuildAutomaton CLI
 *
 * Connects to backend API over WebSocket (workspace DO), runs ACP client for local
 * agents (Cursor CLI, Claude Code, etc.), proxies requests to localhost, and
 * supports skills (e.g. preview: start/stop local preview server).
 *
 * Usage:
 *   npx @buildautomaton/cli
 *   buildautomaton --api-url https://api.example.com --workspace-id <id>
 *   npm run start -- --api-url https://api.example.com --bridge-root /path/to/repo   (use -- so npm forwards args)
 *   buildautomaton --trace …   (trace: git subprocess output and other verbose diagnostics)
 */

import { Command } from 'commander';
import type { CliOptions } from './cli/cli-options.js';
import { CLI_VERSION } from './cli-version.js';
import { DEFAULT_API_URL, DEFAULT_FIREHOSE_URL } from './cli/defaults.js';
import { runCliAction } from './cli/run-cli-action.js';

async function main(): Promise<void> {
  const program = new Command();

  program
    .name('buildautomaton')
    .description('CLI for BuildAutomaton: ACP client, WebSocket bridge to backend, and skills (e.g. preview)')
    .version(CLI_VERSION)
    .option('-u, --api-url <url>', 'Backend API URL', process.env.BUILDAUTOMATON_API_URL ?? DEFAULT_API_URL)
    .option('-w, --workspace-id <id>', 'Workspace ID (or set BUILDAUTOMATON_WORKSPACE_ID)', process.env.BUILDAUTOMATON_WORKSPACE_ID)
    .option('-t, --token <token>', 'Auth token (or set BUILDAUTOMATON_AUTH_TOKEN)', process.env.BUILDAUTOMATON_AUTH_TOKEN)
    .option(
      '--firehose-url <url>',
      'Firehose server URL (default: Fly app; or BUILDAUTOMATON_FIREHOSE_URL / legacy BUILDAUTOMATON_PROXY_URL)',
      process.env.BUILDAUTOMATON_FIREHOSE_URL ?? process.env.BUILDAUTOMATON_PROXY_URL ?? DEFAULT_FIREHOSE_URL,
    )
    .option('--proxy-url <url>', 'Deprecated alias for --firehose-url', undefined)
    .option(
      '--bridge-root <path>',
      'Bridge root directory (absolute or relative to the current directory): git repos, skills, file index, and agent cwd for bridge_root sessions',
    )
    .option(
      '--cwd <path>',
      'Deprecated: same as --bridge-root',
    )
    .option('-n, --name <name>', 'Bridge name when creating via browser (alphanumeric and underscores only)')
    .option(
      '--worktrees-root <path>',
      'Root directory for per-session git worktrees (default: ~/.buildautomaton/worktrees).',
    )
    .option(
      '--e2ee-certificates-dir <path>',
      'Directory to load or generate E2EE keys for sessions, files, and logs',
      process.env.BUILDAUTOMATON_E2EE_CERTIFICATES_DIR,
    )
    .option('--no-config', 'Ignore saved config at ~/.buildautomaton/config.json')
    .option('--debug', 'Print debug-level messages from the CLI')
    .option('--trace', 'Print trace-level messages (e.g. git subprocess output); implies --debug')
    .action(async (opts: CliOptions) => runCliAction(program, opts));

  await program.parseAsync(process.argv);
}

main().catch((err) => {
  console.error(err);
  process.exit(1);
});
